import { useState } from "react";
import {
  TopToolbar,
  useRecordContext,
  Confirm,
  useRedirect,
  useNotify,
  useRefresh,
  usePermissions,
  useGetOne,
  type RaRecord,
} from "react-admin";
import PrintIcon from "@mui/icons-material/Print";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CancelIcon from "@mui/icons-material/Cancel";
import BalanceIcon from "@mui/icons-material/Balance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
} from "@mui/material";
import { HallBookingsCloseBookingModal } from "./HallBookingsCloseBookingModal";
import { callApi } from "@/dataprovider/misc-apis";
import { useShowTotals } from "../BookingShowContext";
import { hasPermission } from "@/utils/permission-utils";

type ConfirmType = "raza" | "writeoff" | "refundSettled" | "delete";
type BookingRecord = RaRecord & {
  id: string;
  razaRequestId?: string | null;
  checkedOutOn?: string | null;
};

const ConfirmConfig: Record<
  ConfirmType,
  { title: string; content: (record: RaRecord | undefined) => string }
> = {
  raza: {
    title: "Confirm Raza Granted",
    content: (rec) =>
      `Are you sure you want to mark Raza as granted for Booking #${rec?.bookingNo as string}?`,
  },
  writeoff: {
    title: "Confirm Write-Off Booking",
    content: (rec) =>
      `Are you sure you want to write-off Booking #${rec?.bookingNo as string}? This cannot be undone.`,
  },
  refundSettled: {
    title: "Confirm Settle Refund",
    content: (rec) =>
      `Are you sure you want to settle the refund for Booking #${rec?.bookingNo as string}?`,
  },
  delete: {
    title: "Confirm Delete Booking",
    content: (rec) =>
      `Are you sure you want to delete Booking #${rec?.bookingNo as string}? Hall dates will be removed from the calendar. Void or delete any receipts first.`,
  },
};

const compactMenuSx = {
  "& .MuiListSubheader-root": {
    lineHeight: 1.4,
    fontSize: "0.7rem",
    fontWeight: 600,
    py: 0.5,
    px: 1.5,
  },
  "& .MuiMenuItem-root": {
    minHeight: 30,
    py: 0.25,
    px: 1.25,
    "& .MuiListItemIcon-root": {
      minWidth: 28,
    },
    "& .MuiListItemText-primary": {
      fontSize: "0.8rem",
      lineHeight: 1.3,
    },
  },
  "& .MuiDivider-root": {
    my: 0.25,
  },
};

export const HallBookingsShowActions = () => {
  const { permissions } = usePermissions();
  const redirect = useRedirect();
  const record = useRecordContext<BookingRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState<{
    type: ConfirmType;
    open: boolean;
    loading: boolean;
  } | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { totalAmountPending } = useShowTotals();
  const linkedRazaRequestId =
    record?.razaRequestId != null && String(record.razaRequestId).trim() !== ""
      ? String(record.razaRequestId)
      : "";
  const { data: linkedRazaRequest } = useGetOne(
    "razaRequests",
    { id: linkedRazaRequestId },
    { enabled: Boolean(linkedRazaRequestId) }
  );
  const linkedRazaGranted = Boolean(linkedRazaRequest?.razaGranted);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleAddReceipt = () => {
    redirect(`/contRcpt/create?bookingId=${record?.id}`);
    handleMenuClose();
  };
  const handlePrintRazaForm = () => {
    window.open(`#/raza-print/${record?.id}`, "_blank");
    handleMenuClose();
  };
  const handleMarkRaza = () => {
    setConfirmConfig({ type: "raza", open: true, loading: false });
    handleMenuClose();
  };
  const handlePrintConfirmationVoucher = () => {
    window.open(`#/confirmation-voucher/${record?.id}`, "_blank");
    handleMenuClose();
  };
  const handleOpenCloseEvent = () => {
    setCloseModalOpen(true);
    handleMenuClose();
  };
  const handleWriteOff = () => {
    setConfirmConfig({
      type: "writeoff",
      open: true,
      loading: false,
    });
    handleMenuClose();
  };
  const handleSettleRefund = () => {
    setConfirmConfig({
      type: "refundSettled",
      open: true,
      loading: false,
    });
    handleMenuClose();
  };
  const handleDeleteBooking = () => {
    setConfirmConfig({
      type: "delete",
      open: true,
      loading: false,
    });
    handleMenuClose();
  };

  const handleConfirm = async () => {
    if (!record?.id || !confirmConfig) return;

    if (confirmConfig.type === "raza") {
      if (!linkedRazaRequestId) {
        notify("Link a raza request first", { type: "warning" });
        return;
      }
      await callApi({
        location: `razaRequests/${linkedRazaRequestId}/grant-raza`,
        method: "PUT",
      })
        .then(() => {
          notify("Raza marked successfully", { type: "success" });
        })
        .catch(() => {
          notify("Something went wrong", { type: "error" });
        });
    } else if (confirmConfig.type === "writeoff") {
      await callApi({
        location: `bookings/${record.id}/write-off`,
        method: "PUT",
      })
        .then(() => {
          notify("Writeoff successfully", { type: "success" });
        })
        .catch(() => {
          notify("Something went wrong", { type: "error" });
        });
    } else if (confirmConfig.type === "refundSettled") {
      await callApi({
        location: `bookings/${record.id}/settle-refund`,
        method: "PUT",
      })
        .then(() => {
          notify("Refund settled successfully", { type: "success" });
        })
        .catch(() => {
          notify("Something went wrong", { type: "error" });
        });
    } else if (confirmConfig.type === "delete") {
      await callApi({
        location: `bookings/${record.id}`,
        method: "DELETE",
      })
        .then(() => {
          notify("Booking deleted successfully", { type: "success" });
          redirect("/hallBookings");
        })
        .catch((e: unknown) => {
          const message = (e as { response?: { data?: { message?: string } } })?.response?.data
            ?.message;
          notify(
            typeof message === "string" && message.trim() ? message.trim() : "Something went wrong",
            { type: "error" }
          );
        });
    }
    refresh();
    setConfirmConfig(null);
  };

  const onCloseEvent = async (data: {
    actualThaals: Record<string, number>;
    extraExpenses: number;
    remarks: string;
  }) => {
    if (!record?.id) return;
    await callApi({
      location: `bookings/${record.id}/close`,
      data: {
        actualThaals: data.actualThaals,
        extraExpenses: data.extraExpenses,
        remarks: data.remarks,
      },
      method: "PUT",
    })
      .then(() => {
        notify("Raza marked successfully", { type: "success" });
      })
      .catch(() => {
        notify("Something went wrong", { type: "error" });
      });

    refresh();
    setCloseModalOpen(false);
  };

  if (!record) return null;

  return (
    <TopToolbar
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        mt: 0,
      }}
    >
      <Box>
        <Button
          variant="outlined"
          onClick={handleMenuClick}
          endIcon={<ArrowDropDownIcon />}
          sx={{ textTransform: "none" }}
        >
          Actions
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          slotProps={{ paper: { sx: compactMenuSx } }}
        >
          <ListSubheader disableSticky>Receipts</ListSubheader>
          <MenuItem onClick={handleAddReceipt}>
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Receipt</ListItemText>
          </MenuItem>

          <Divider />
          <ListSubheader disableSticky>Print</ListSubheader>
          {!linkedRazaGranted && (
            <MenuItem onClick={handlePrintRazaForm}>
              <ListItemIcon>
                <PrintIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Raza form</ListItemText>
            </MenuItem>
          )}
          {linkedRazaGranted && (
            <MenuItem onClick={handlePrintConfirmationVoucher}>
              <ListItemIcon>
                <ReceiptIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Print Confirmation Voucher</ListItemText>
            </MenuItem>
          )}

          <Divider />
          <ListSubheader disableSticky>Booking Status</ListSubheader>
          {linkedRazaRequestId && !linkedRazaGranted && (
            <MenuItem onClick={handleMarkRaza}>
              <ListItemIcon>
                <DoneAllIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Mark Raza</ListItemText>
            </MenuItem>
          )}
          {!record.checkedOutOn && (
            <MenuItem onClick={handleOpenCloseEvent}>
              <ListItemIcon>
                <EventAvailableIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Close Event</ListItemText>
            </MenuItem>
          )}

          {(hasPermission(permissions, "writeoff.allow") && totalAmountPending > 0) ||
          !record.refundReturnedOn ? (
            <>
              <Divider />
              <ListSubheader disableSticky>Financial</ListSubheader>
              {hasPermission(permissions, "writeoff.allow") && totalAmountPending > 0 ? (
                <MenuItem onClick={handleWriteOff}>
                  <ListItemIcon>
                    <CancelIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText>Write-Off Booking</ListItemText>
                </MenuItem>
              ) : null}
              {!record.refundReturnedOn ? (
                <MenuItem onClick={handleSettleRefund}>
                  <ListItemIcon>
                    <BalanceIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Settle Refund</ListItemText>
                </MenuItem>
              ) : null}
            </>
          ) : null}

          <Divider />
          <MenuItem onClick={handleDeleteBooking}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Booking</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      <HallBookingsCloseBookingModal
        open={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        onSubmit={(data) => {
          void onCloseEvent(data);
        }}
        record={record}
      />

      <Confirm
        isOpen={!!confirmConfig?.open}
        loading={confirmConfig?.loading}
        title={confirmConfig ? ConfirmConfig[confirmConfig.type].title : ""}
        content={confirmConfig ? ConfirmConfig[confirmConfig.type].content(record) : ""}
        onConfirm={() => void handleConfirm()}
        onClose={() => setConfirmConfig(null)}
      />
    </TopToolbar>
  );
};

export default HallBookingsShowActions;
